import React, { PropsWithChildren, useMemo } from 'react'
import { Heading as AltoHeading, Box, Section } from '@alto-avios/alto-ui'
import Image from 'next/image'
import { deviceSizes } from '~/styles/deviceSizes'
import { ContentfulImage, ImageSize, isContentfulUrl } from '../ContentfulImage'

import styles from './Hero.module.scss'

interface HeroProps extends React.HTMLAttributes<HTMLDivElement> {
  imgSrc: string
  imgAlt?: string
  overlayContent?: React.ReactNode
}

const heroImageSizes: ImageSize[] = [
  {
    width: 1440,
    height: 400,
    media: `(min-width: ${deviceSizes.lg})`,
    densities: [1, 2],
  },
  {
    width: 1280,
    height: 400,
    media: `(min-width: ${deviceSizes.md})`,
    densities: [1, 2],
  },
  {
    width: 960,
    height: 400,
    media: `(min-width: ${deviceSizes.sm})`,
    densities: [1, 2],
  },
  {
    width: 650,
    height: 228,
    media: `(min-width: ${deviceSizes.xs})`,
    densities: [1, 2],
  },
  {
    width: 475,
    height: 228,
    media: `(max-width: ${deviceSizes.xs})`,
    densities: [1, 2],
  },
]

function Heading({ children }: Readonly<PropsWithChildren>) {
  return (
    <AltoHeading as="h1" size="xl" foregroundColor="accentSecondary" textAlign="center">
      {children}
    </AltoHeading>
  )
}

function SubHeading({ children }: Readonly<PropsWithChildren>) {
  return (
    <AltoHeading as="h2" size="md" foregroundColor="accentSecondary" textAlign="center">
      {children}
    </AltoHeading>
  )
}

function Tagline({ children }: Readonly<PropsWithChildren>) {
  return (
    <Box className={styles.tagline} textAlign="center">
      {children}
    </Box>
  )
}

function Hero({ imgSrc, imgAlt = '', overlayContent, children }: PropsWithChildren<HeroProps>) {
  const heroImage = useMemo(() => {
    if (!imgSrc) {
      return <div className={styles.picture} data-testid="hero-image" />
    }
    if (isContentfulUrl(imgSrc)) {
      return (
        <ContentfulImage
          src={imgSrc}
          sizes={heroImageSizes}
          alt={imgAlt}
          data-testid="hero-image"
          className={styles.picture}
          imageClassName={styles.image}
        />
      )
    }

    return (
      <Image
        src={imgSrc}
        alt={imgAlt}
        className={styles.image}
        data-testid="hero-image"
        fill
        unoptimized
        style={{ objectFit: 'cover', objectPosition: 'center' }}
      />
    )
  }, [imgSrc, imgAlt])

  return useMemo(
    () => (
      <Box
        dataTestId="hero-component"
        paddingBottom="lg"
        position="relative"
        zIndex="20"
        width="100%"
      >
        <Section
          id="Hero"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          backgroundColor="transparent"
          paddingY={0}
        >
          <div className={styles.imageContainer}>{heroImage}</div>
          <Section.Container containerMaxWidth="page">
            <div className={overlayContent ? styles.contentGroupWithOverlay : styles.contentGroup}>
              {overlayContent ? <div className={styles.overlay}>{overlayContent}</div> : null}
              <Box
                className={styles.content}
                position="relative"
                textAlign="center"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                gap="xs"
                padding="lg"
                backgroundColor="base"
                borderRadius="md"
                borderWidth="xs"
                borderStyle="solid"
                borderColour="secondary"
                width="100%"
              >
                {children}
              </Box>
            </div>
          </Section.Container>
        </Section>
      </Box>
    ),
    [heroImage, overlayContent, children]
  )
}

Hero.Heading = Heading
Hero.Tagline = Tagline
Hero.SubHeading = SubHeading

export default Hero
